/**
 * Converts a var name to the exact css var name we use following our naming convention
 *
 * Use this if you want the name of the variable
 *
 * @param name: short name of the variable we refer to
 * @returns actual name of the variable in the dom.
 */
export const toThemeVar = (name: string) => {
  return `--hl-bs--${name}`;
};

/**
 * Converts a var name to the css-var with the var() wrapper
 *
 * Use this if you want to use a variable in e.g. a style block
 *
 * e.g. <div style={{ color: applyThemeVar('primary') }} />
 *
 * @param name: short name of the variable we refer to
 * @returns actual name of the variable in the dom in var().
 */
export const applyThemeVar = (name: string) => {
  return `var(${toThemeVar(name)})`;
};

/**
 * Programmatically gets the theme css variable value for a particular css variable
 *
 * Use this if you are using a component that expects a hex/rgb value.
 *
 * e.g. <ColorPicker color={getThemeVarValue('primary')} />
 *
 * @param name: string name of the variable
 * @param element?: HTMLElement the scope for which the variab le is calculated.
 *                  usually don't pass in anything to get global theme variables.
 * @returns value of the variable.
 */
export const getThemeVarValue = (name: string, element?: HTMLElement) => {
  return getComputedStyle(element || document.body).getPropertyValue(toThemeVar(name));
};

/**
 * These 2 are the similar to plain theme variants above, but formatted/restricted with button values
 */

type ButtonThemes =
  | 'primary'
  | 'secondary'
  | 'link'
  | 'ghost'
  | 'alt-primary'
  | 'alt-secondary'
  | 'alt-link'
  | 'alt-ghost';

type ButtonProp = 'text' | 'text-hover' | 'bg' | 'bg-hover' | 'border' | 'border-hover' | 'outline';

export const toButtonVar = (theme: ButtonThemes, prop: ButtonProp, rgb = false) => {
  return `--hl-bs--btn-${theme}-${prop}${rgb ? '-rgb' : ''}`;
};

export const applyButtonVar = (theme: ButtonThemes, prop: ButtonProp, rgb = false) => {
  return `var(${toButtonVar(theme, prop, rgb)})`;
};

export const getButtonVarValue = (theme: ButtonThemes, prop: ButtonProp, rgb = false, element?: HTMLElement) => {
  return getComputedStyle(element || document.body).getPropertyValue(toButtonVar(theme, prop, rgb));
};
